import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import BlogLayout from "../components/BlogLayout";
import Seo from "../components/Seo";
import { BLOG_POSTS } from "../data/blog";

const fmtDate = (d) => new Date(d).toLocaleDateString("hu-HU", { year: "numeric", month: "long", day: "numeric" });

export default function Blog() {
  const [featured, ...rest] = BLOG_POSTS;
  return (
    <BlogLayout>
      <Seo
        title="Blog – Víz- és fűtésszerelési tippek | szuper-szaki.hu"
        description="Hasznos tippek víz- és fűtésszereléshez: csöpögő csap, WC-tartály, radiátor légtelenítés, csőtörés és fűtés-karbantartás a Szuper Szakitól."
        path="/blog"
      />
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <div className="max-w-2xl mb-12">
          <span className="text-xs sm:text-sm uppercase tracking-widest font-bold text-sky-600 bg-sky-50 px-3 py-1 rounded-full inline-block mb-4 border border-sky-100">
            Szakmai blog
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            Tippek és tanácsok <span className="text-sky-600">víz- és fűtésszereléshez</span>
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Gyakorlati útmutatók, amelyekkel megelőzheti a bajt – és felismeri, mikor érdemes szakembert hívni.
          </p>
        </div>

        {/* featured */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Link
            to={`/blog/${featured.slug}`}
            data-testid="blog-featured-card"
            className="group grid md:grid-cols-2 gap-6 rounded-3xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-sky-200 transition-all mb-10"
          >
            <div className="overflow-hidden h-60 md:h-auto">
              <img src={featured.cover} alt={featured.coverAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-7 lg:p-9 flex flex-col justify-center">
              <div className="flex items-center gap-3 text-xs font-semibold text-slate-400 mb-3">
                <span className="text-sky-600 bg-sky-50 px-2.5 py-1 rounded-full border border-sky-100">{featured.category}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmtDate(featured.date)}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{featured.readMin} perc</span>
              </div>
              <h2 className="font-display text-2xl lg:text-3xl font-extrabold text-slate-900 group-hover:text-sky-700 transition-colors">{featured.title}</h2>
              <p className="mt-3 text-slate-600">{featured.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sky-700 font-bold group-hover:gap-3 transition-all">
                Tovább olvasom <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </motion.div>

        {/* grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
            >
              <Link to={`/blog/${p.slug}`} data-testid="blog-post-card" className="group block rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-sky-200 transition-all h-full">
                <div className="h-44 overflow-hidden">
                  <img src={p.cover} alt={p.coverAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-400 mb-2">
                    <span className="text-sky-600">{p.category}</span>
                    <span>·</span>
                    <span>{p.readMin} perc</span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-slate-900 group-hover:text-sky-700 transition-colors leading-snug">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </BlogLayout>
  );
}
